import React, { useState } from 'react';
import { Upload, Button, message } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { API_BASE_URL } from '../providers/dataProvider';


interface ImageUploadFieldProps {
  value?: string;
  onChange?: (url: string) => void;
  label?: string;
  /** Upload subfolder, e.g. 'restaurant', 'meal' */
  subPath?: string;
  /** Max width of preview thumbnail */
  previewWidth?: number;
  previewHeight?: number;
}

/**
 * A reusable image upload field that works with Ant Design Form.Item.
 * Uploads via POST /media/upload and stores the returned URL as the field value.
 */
export const ImageUploadField: React.FC<ImageUploadFieldProps> = ({
  value,
  onChange,
  previewWidth = 160,
  previewHeight = 120,
}) => {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const token = localStorage.getItem('access_token');
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API_BASE_URL}/media/upload`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || `Upload failed (${res.status})`);
      }

      // data.url is the full public URL
      onChange?.(data.url);
      message.success('Image uploaded');
    } catch (err: any) {
      message.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    onChange?.('');
  };

  // Show preview if value exists
  if (value) {
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div
          style={{
            position: 'relative',
            width: previewWidth,
            height: previewHeight,
            borderRadius: 8,
            overflow: 'hidden',
            border: '1px solid #e8e8e8',
            flexShrink: 0,
          }}
        >
          <img
            src={value}
            alt="Preview"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <a href={value} target="_blank" rel="noreferrer" style={{ fontSize: 12, wordBreak: 'break-all', maxWidth: 280 }}>
            {value.length > 60 ? value.substring(0, 60) + '...' : value}
          </a>
          <div style={{ display: 'flex', gap: 8 }}>
            <Upload
              accept="image/*"
              showUploadList={false}
              beforeUpload={(file: any) => {
                handleUpload(file as unknown as File);
                return false;
              }}
            >
              <Button size="small" loading={uploading}>Replace</Button>
            </Upload>
            <Button size="small" danger icon={<DeleteOutlined />} onClick={handleRemove}>
              Remove
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // No image — show upload area
  return (
    <Upload
      accept="image/*"
      showUploadList={false}
      beforeUpload={(file: any) => {
        handleUpload(file as unknown as File);
        return false;
      }}
    >
      <div
        style={{
          width: previewWidth,
          height: previewHeight,
          borderRadius: 8,
          border: '1px dashed #434343',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          color: '#898989',
          transition: 'border-color 0.2s',
        }}
      >
        <PlusOutlined style={{ fontSize: 24, marginBottom: 4 }} />
        <span style={{ fontSize: 12 }}>{uploading ? 'Uploading...' : 'Upload'}</span>
      </div>
    </Upload>
  );
};
